import { useMutation, useQuery } from "@apollo/client";
import {
  Button,
  Card,
  Drawer,
  Grid,
  LoadingOverlay,
  ScrollArea,
  Select,
  Stack,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { showNotification } from "@mantine/notifications";
import {
  ASSIGN_DRIVER_TO_DROPOOF,
  RECALL_DRIVER_FOR_PENDING_DROPOFF,
} from "apollo/mutuations";
import { GET_DRIVERS, getDropoffsQuery } from "apollo/queries";
import ActionButton from "components/controls/ActionButtonNew";
import { DropOffAddModal } from "components/Dropoff/DropOffAddModal";
import ManageDropOffModal from "components/Dropoff/ManageDropOffModal";
import DriverMapView from "components/Dropoff/TrackDirection";
import B2bTable from "components/reusable/b2bTable";
import { customLoader } from "components/utilities/loader";
import Pusher from "pusher-js";
import { useEffect, useState } from "react";
import { FiEye, FiUserPlus } from "react-icons/fi";
import { IoIosCall } from "react-icons/io";
import { rolePermissions } from "utiles/permissions";
import { formatNumber } from "utiles/url";
import DropOffCard from "./card";

const DropOffs = () => {
  const [size, setSize] = useState("10");
  const [opened, setOpened] = useState(false);
  const [openedEdit, setOpenedEdit] = useState(false);
  const [openedAssign, setOpenedAssigned] = useState(false);
  const [editId, setEditId] = useState();
  const [callingDropoffs, setCallingDropoffs] = useState({}); // Track calling state per drop-off
  const [activePage, setActivePage] = useState(1);
  const [total, setTotal] = useState(0);
  const [openedLocation, setOpenedLocation] = useState(false);
  const [dropoffStatus, setDropoffStatus] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [dropOffs, setDropOffs] = useState([]);
  const [confirmedSearch, setConfirmedSearch] = useState("");
  const roles = JSON.parse(localStorage.getItem("roles")) || [];
  const hasSupplierPermission = roles.some(
    (permission) => permission === "supplier",
  );
  const hasManagerPermission = roles.some(
    (permission) => permission === "warehouse_manager",
  );
  const role =
    rolePermissions.hasAdmin(roles) || rolePermissions.hasHoStaff(roles)
      ? "admin"
      : hasSupplierPermission
        ? "supplier"
        : "warehouse_manager";
  const GET_DROPOFFS = getDropoffsQuery(role);

  const { data, loading, refetch } = useQuery(GET_DROPOFFS, {
    variables: {
      search: searchValue,
      first: parseInt(size),
      page: activePage,
      status: dropoffStatus,
      ordered_by: [],
    },
    onError(error) {
      showNotification({
        color: "red",
        title: "Error",
        message:
          error?.graphQLErrors?.[0]?.message || "Failed to fetch Drop-offs",
      });
    },
  });

  const handlePageSizeChange = (newSize) => {
    setSize(newSize);
    setActivePage(1);
  };

  useEffect(() => {
    if (data) {
      const queryKey =
        rolePermissions.hasAdmin(roles) || rolePermissions.hasHoStaff(roles)
          ? "dropoffs"
          : hasSupplierPermission
            ? "supplierDropoffs"
            : "warehouseManagerDropoffs";
      setTotal(data[queryKey].paginatorInfo.lastPage);
      setDropOffs(data[queryKey].data || []);
    }
  }, [
    data,
    size,
    rolePermissions.hasAdmin(roles),
    rolePermissions.hasHoStaff(roles),
    hasSupplierPermission,
  ]);

  const handleChange = (currentPage) => {
    setActivePage(currentPage);
  };

  useEffect(() => {
    refetch();
    localStorage.removeItem("dropoffs");
    window.dispatchEvent(new Event("dropoffs-changed"));
  }, []);

  const handleGeoLocationClick = (id) => {
    setOpenedLocation(true);
    setEditId(id);
  };

  const headerData = [
    {
      label: "Retailer",
      key: "retailer",
      sortable: false,
      searchable: true,
      render: (rowData) => (
        <span>{rowData.dropoff_order[0].order.retailer.name}</span>
      ),
    },
    {
      label: "Warehouse",
      key: "warehouse",
      sortable: false,
      searchable: true,
      render: (rowData) => {
        return <span>{rowData?.dropoff_order[0].order?.warehouse?.name}</span>;
      },
    },
    {
      label: "Driver",
      key: "driver",
      sortable: false,
      searchable: true,
      render: (rowData) => (
        <span>
          {rowData.driver !== null
            ? rowData.driver?.user?.name
            : "Not Assigned"}
        </span>
      ),
    },
    {
      label: "Order Code",
      key: "order_code",
      sortable: false,
      searchable: true,
      render: (rowData) => {
        return <span>{rowData?.dropoff_order[0]?.order?.order_code}</span>;
      },
    },
    {
      label: "Cost",
      key: "cost",
      sortable: false,
      searchable: true,
      render: (rowData) => (
        <div style={{ width: "10%" }}>{formatNumber(rowData.cost)}</div>
      ),
    },
    {
      label: "Date",
      key: "created_at",
      sortable: true,
      searchable: false,
      render: (rowData) => <span>{rowData.created_at}</span>,
    },
    {
      label: "Actions",
      key: "actions",
      sortable: false,
      searchable: false,
      width: "17%",
      render: (rowData) => (
        <div style={{ display: "flex", gap: "6px" }}>
          <ActionButton
            label="View"
            color="success"
            onClick={() => handleManageDropOff(`${rowData.id}`)}
          >
            <FiEye size={15} />
          </ActionButton>
          {rowData.status === "PENDING" && hasManagerPermission && (
            <>
              {callingDropoffs[rowData.id] ? (
                <ActionButton label="Calling" color="call"></ActionButton>
              ) : (
                <ActionButton
                  label="Call"
                  color="call"
                  onClick={() => handleRecallDriver(`${rowData.id}`)}
                >
                  <IoIosCall size={15} />
                </ActionButton>
              )}
            </>
          )}
          {rowData.status === "MISSED_CALL" &&
            (rolePermissions.hasAdmin(roles) ||
              rolePermissions.hasHoStaff(roles)) && (
              <ActionButton
                color="assign"
                label="Assign Driver"
                onClick={() => handleAssignDriver(`${rowData.id}`)}
              >
                <FiUserPlus fontSize="medium" />
              </ActionButton>
            )}
        </div>
      ),
    },
  ];

  const [recallDriverForPendingDropoff] = useMutation(
    RECALL_DRIVER_FOR_PENDING_DROPOFF,
    {
      refetchQueries: [
        {
          query: GET_DROPOFFS,
          variables: {
            first: parseInt(size),
            page: activePage,
            status: dropoffStatus,
            search: searchValue,
            ordered_by: [],
          },
        },
      ],
      onCompleted(data) {
        setCallingDropoffs((prev) => ({
          ...prev,
          [data.recallDriverForPendingDropoff.id]: false,
        }));
        showNotification({
          color: "green",
          title: "Success",
          message: "Driver recalled successfully!",
        });
      },
      onError(error) {
        setCallingDropoffs((prev) => ({
          ...prev,
          [error?.variables?.dropoff_id]: false,
        }));
        showNotification({
          color: "red",
          title: "Error",
          message: `${error.message}`,
        });
      },
    },
  );

  const [assignDriverToDropoff, { loading: assignLoading }] = useMutation(
    ASSIGN_DRIVER_TO_DROPOOF,
    {
      refetchQueries: [
        {
          query: GET_DROPOFFS,
          variables: {
            first: parseInt(size),
            page: activePage,
            status: dropoffStatus,
            search: searchValue,
            ordered_by: [],
          },
        },
      ],
      onCompleted(data) {
        setOpenedAssigned(false);
        showNotification({
          color: "green",
          title: "Success",
          message: "Driver assigned successfully!",
        });
      },
      onError(error) {
        setCallingDropoffs((prev) => ({
          ...prev,
          [error?.variables?.dropoff_id]: false,
        }));
        showNotification({
          color: "red",
          title: "Error",
          message: `${error.message}`,
        });
      },
    },
  );

  const handleRecallDriver = (id) => {
    setCallingDropoffs((prev) => ({ ...prev, [id]: true }));
    recallDriverForPendingDropoff({ variables: { dropoff_id: id } });
  };

  const handleAssignDriver = (id) => {
    setOpenedAssigned(true);
    setEditId(id);
  };

  const handleManageDropOff = (id) => {
    setOpenedEdit(true);
    setEditId(id);
  };

  const submit = () => {
    const driverId = form.values.driver_id;
    assignDriverToDropoff({
      variables: {
        dropoff_id: parseInt(editId),
        driver_id: parseInt(driverId),
      },
    });
  };

  useEffect(() => {
    const pusher = new Pusher("83f49852817c6b52294f", {
      cluster: "mt1",
    });

    const notificationChannel = pusher.subscribe("notification");

    notificationChannel.bind("new-item-created", function (newOrder) {
      refetch()
        .then(({ data }) => {
          const queryKey =
            rolePermissions.hasAdmin(roles) || rolePermissions.hasHoStaff(roles)
              ? "dropoffs"
              : hasSupplierPermission
                ? "supplierDropoffs"
                : "warehouseManagerDropoffs";
          const updatedDropOff = data[queryKey].data || [];
          setTotal(data[queryKey].paginatorInfo.lastPage);
          setDropOffs(updatedDropOff);
        })
        .catch((error) => {
          console.error("Error fetching updated orders:", error);
        });
    });

    return () => {
      pusher.disconnect();
    };
  }, [
    refetch,
    rolePermissions.hasAdmin(roles),
    rolePermissions.hasHoStaff(roles),
    hasSupplierPermission,
  ]);

  const clearFilter = () => {
    setDropoffStatus(null);
    setSearchValue("");
    setConfirmedSearch("");
  };

  const handleManualSearch = (searchTerm) => {
    setSearchValue(searchTerm);
  };

  const clearInput = () => {
    setSearchValue("");
    setDropoffStatus(null);
    setConfirmedSearch("");
  };

  const form = useForm({
    initialValues: {
      driver_id: null,
    },
  });

  const [drivers, setDriverDropDownData] = useState([]);
  const { loading: driver_loading } = useQuery(GET_DRIVERS, {
    variables: { first: 2000, page: 1 },
    skip: !(
      rolePermissions.hasAdmin(roles) || rolePermissions.hasHoStaff(roles)
    ),
    onCompleted(data) {
      const newArr = data.drivers.data.map((element) => ({
        label: element.user.name,
        value: element.id,
      }));
      setDriverDropDownData(newArr);
    },
    onError(error) {
      showNotification({
        color: "red",
        title: "Error",
        message: error?.graphQLErrors?.[0]?.message || "Failed to get drivers",
      });
    },
  });

  const handleCategoryChange = (val) => {
    form.setFieldValue("driver_id", val);
  };

  return loading ? (
    <LoadingOverlay
      visible={loading}
      color="blue"
      overlayBlur={2}
      loader={customLoader}
    />
  ) : (
    <div style={{ width: "98%" }}>
      <Drawer
        opened={openedEdit}
        onClose={() => setOpenedEdit(false)}
        padding="xl"
        size="50%"
        position="right"
        styles={{
          closeButton: {
            color: "black",
            marginTop: "50px",
          },
        }}
      >
        <ManageDropOffModal setOpenedEdit={setOpenedEdit} editId={editId} />
      </Drawer>
      <Drawer
        opened={opened}
        onClose={() => setOpened(false)}
        title="Adding a DropOffs"
        padding="xl"
        size="80%"
        position="bottom"
      >
        <DropOffAddModal
          total={total}
          setTotal={setTotal}
          activePage={activePage}
          setActivePage={setActivePage}
          setOpened={setOpened}
        />
      </Drawer>
      <Drawer
        opened={openedLocation}
        onClose={() => setOpenedLocation(false)}
        title="Warehouse Location"
        padding="xl"
        size="80%"
        position="bottom"
      >
        <DriverMapView id={editId} />
      </Drawer>
      <Card shadow="sm" p="lg">
        <DropOffCard
          onCardClick={setDropoffStatus}
          handelSearch={setSearchValue}
          clearFilter={clearFilter}
          selectedStatus={dropoffStatus}
        />
        <Drawer
          opened={openedAssign}
          onClose={() => setOpenedAssigned(false)}
          styles={{
            closeButton: {
              color: "black",
              marginTop: "50px",
            },
          }}
          padding="xl"
          position="right"
          size="40%"
        >
          <form onSubmit={form.onSubmit(() => submit())}>
            <Stack>
              <Grid>
                <Grid.Col span={12}>
                  <Select
                    searchable
                    required
                    clearable
                    data={drivers}
                    value={form.values.driver_id}
                    onChange={handleCategoryChange}
                    label="Select Driver"
                    placeholder="Pick a driver to assign"
                    disabled={driver_loading}
                  />
                </Grid.Col>
              </Grid>
              <Grid>
                <Grid.Col span={12}>
                  <Button
                    style={{
                      width: "30%",
                      backgroundColor: "#FF6A00",
                      color: "#FFFFFF",
                    }}
                    type="submit"
                    color="blue"
                    disabled={assignLoading}
                    loading={assignLoading}
                  >
                    Submit
                  </Button>
                </Grid.Col>
              </Grid>
            </Stack>
          </form>
        </Drawer>
        <ScrollArea>
          <B2bTable
            total={total}
            paginatorInfo={
              data?.[
                rolePermissions.hasAdmin(roles) ||
                rolePermissions.hasHoStaff(roles)
                  ? "dropoffs"
                  : hasSupplierPermission
                    ? "supplierDropoffs"
                    : "warehouseManagerDropoffs"
              ]?.paginatorInfo
            }
            activePage={activePage}
            handleChange={handleChange}
            header={headerData}
            clearInput={clearInput}
            handelSearch={handleManualSearch}
            searchValue={confirmedSearch}
            onSearchChange={setConfirmedSearch}
            loading={loading}
            data={
              dropOffs.length
                ? dropOffs
                : (data &&
                    data[
                      rolePermissions.hasAdmin(roles) ||
                      rolePermissions.hasHoStaff(roles)
                        ? "dropoffs"
                        : hasSupplierPermission
                          ? "supplierDropoffs"
                          : "warehouseManagerDropoffs"
                    ]?.data) ||
                  []
            }
            size={size}
            handlePageSizeChange={handlePageSizeChange}
          />
        </ScrollArea>
      </Card>
    </div>
  );
};

export default DropOffs;
